/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import classNames from 'classnames';
import { useMemo } from 'react';
import { ProductCard } from '../../components/ProductCard/ProductCard';
import { ProductCardSkeleton } from '../../components/ProductCard/ProductCardSkeleton';
import { useCmsHashState } from '../../hooks/useHashState';
import { AttrPlainText } from '../../types/attributes';
import { ATTR_NAMES, getAttrFromProduct } from '../../types/attrNames';
import { CmsProduct } from '../../types/cmsProduct';
import { MetadataProductCard, PriceSource } from '../../types/cmsTypes';
import { TravelPremiumsData } from '../../types/travelInsurance';
import { sortProductsByApprovalOdds } from '../../utils/approvalOddsSorting';
import { resolveProductTag } from '../../utils/helpers';
import { parsePriceValue } from '../../utils/parsePriceValue';
import { RankedCard, rankProductCards } from '../../utils/productRanking';
import {
  arePremiumsPending,
  pendingSkeletonCount,
} from '../../utils/travelInsurance/premiumsPending';
import { applyProductFilter } from '../SortAndFilterPanel/applyProductFilter';
import { applyProductSort } from '../SortAndFilterPanel/applyProductSort';
import { FilterEmptyState } from '../SortAndFilterPanel/FilterEmptyState';
import { useProductEligibilityState } from '../SortAndFilterPanel/ProductEligibilityProvider';
import { useProductFilterState } from '../SortAndFilterPanel/ProductFilterProvider';
import { useActiveProductSort } from '../SortAndFilterPanel/ProductSortProvider';
import { isPlayedDown } from '../SortAndFilterPanel/resolveEligibility';
import { CardListingPositionProps } from './types';

export const CardListingPosition: React.FC<CardListingPositionProps> = ({
  metadata,
  pageType,
  vertical,
  cardPosition,
  url,
  initialLoanSearchParams,
  calculateError,
  productWithLivePrice,
  carInsuranceQuotes,
  travelPremiumsData,
  travelQuotes,
  statusCarInsuranceRequest,
  skeletonThreshold,
  pushDataLayer,
  plFilterConfig,
  approvalOddsData,
  approvalOddsLabels,
  onApprovalOddsUnlockClick,
  onApprovalOddsRefreshClick,
  productDetailsModalKeys,
  getPdpModalConfigAction,
  isPreview,
  renderPdpModalHero,
}) => {
  const mobileVisible = classNames({
    'hidden md:block': !cardPosition.visible_mobile,
  });

  // QA/design validation overlay (CMS-1862): open the page with
  // `#cms_rankingSource=true` to see each card's ranking source and the
  // manual/auto price divider. Hash state is client-only, so SSR is unaffected.
  const [{ rankingSource: showRankingSource }] = useCmsHashState<{
    rankingSource: boolean;
  }>({ rankingSource: 'boolean' });

  let cardsAreShown = 0;
  // Memoised because `defaultOrder` depends on them: a fresh array each render
  // would re-sort the list on every unrelated re-render.
  const products = useMemo(
    () => metadata?.product_card ?? [],
    [metadata?.product_card],
  );

  const productsTravelInsurance: MetadataProductCard[] = useMemo(() => {
    if (vertical !== 'TRAVEL_INSURANCE') return [];

    return products.filter((p) => {
      const product = p.productBlock?.product;

      const travelInsuranceData: TravelPremiumsData[number] | undefined =
        getTravelInsuranceDataFromProduct(product, travelPremiumsData);

      if (travelInsuranceData) {
        return true;
      } else return false;
    });
  }, [vertical, products, travelPremiumsData]);

  // TI ranking (CMS-1862): the quotes response only contains products of the
  // active trip type, so the premium filter above already yields the per-type
  // view. Pinned cards keep the PCT drag order; the rest is the auto pool.
  // Memoised because `defaultOrder` depends on it.
  const rankedTravelInsurance: RankedCard<MetadataProductCard>[] =
    useMemo(() => {
      if (vertical !== 'TRAVEL_INSURANCE') return [];

      const rankableTi = productsTravelInsurance.filter((card) => card.visible);

      // Only the pool is ever ranked — a pinned card's price is never compared —
      // so everything below looks at the pool alone.
      const poolTi = rankableTi.filter((card) => !card.pinned);

      // Mid re-quote the wrapper mints placeholder premiums (empty string) for
      // the products already on screen while the resolved ones carry a real
      // premium. Ranking those together mixes two scales — a "from" static price
      // against a real quote — so the not-yet-quoted cards would take the cheapest
      // slots and drop as their premium lands. While that is happening the whole
      // pool ranks on the static scale, then switches to premium order in one step.
      //
      // Gated on the quote status rather than on "some premium is unparseable":
      // in a settled response an unpriced card is a fact, not a transient, and
      // must sink rather than pin the whole pool to the static scale forever.
      //
      // Which scale to fall back to is decided by coverage: swap to the static one
      // only while it can order more of the pool than the premiums currently can.
      // Requiring a static price for *every* card was worse than useless — with
      // none authored both scales are equally empty, so it changed nothing, and
      // with most authored it threw away a usable partial order.
      const requoteInFlight = travelQuotes?.status === 'inprogress';
      const poolPremiumCount = poolTi.filter(
        (card) => getLivePremium(card, travelPremiumsData) !== undefined,
      ).length;
      const poolStaticPriceCount = poolTi.filter(
        (card) => getStaticPrice(card) !== undefined,
      ).length;

      const priceSource: PriceSource =
        metadata?.priceSource === 'LIVE_PREMIUM' &&
        requoteInFlight &&
        poolStaticPriceCount > poolPremiumCount
          ? 'STATIC_PRICE'
          : (metadata?.priceSource ?? 'STATIC_PRICE');

      return rankProductCards({
        cards: rankableTi,
        isPinned: (card) => !!card.pinned,
        getPrice: (card) =>
          getTravelCardPrice(card, travelPremiumsData, priceSource),
        fallbackEnabled: metadata?.priceFallbackRanking ?? false,
        direction: metadata?.priceSortDirection,
      });
    }, [
      vertical,
      productsTravelInsurance,
      travelQuotes?.status,
      travelPremiumsData,
      metadata?.priceSource,
      metadata?.priceFallbackRanking,
      metadata?.priceSortDirection,
    ]);

  const travelRankingByCard = useMemo(
    () => new Map(rankedTravelInsurance.map((ranked) => [ranked.card, ranked])),
    [rankedTravelInsurance],
  );

  // From the context, not a prop: each vertical's wrapper would otherwise have
  // to remember to pass it.
  const productSortOption = useActiveProductSort();
  const { categories, selection } = useProductFilterState();
  // Marks the cards; deliberately not part of `productForRender` below, which
  // is the filtered and ordered set. Eligibility removes nothing and reorders
  // nothing (AC 7).
  const { verdictFor } = useProductEligibilityState();

  // Filtered before sorting, so the ordered set is the rendered set; the sort is
  // stable, so the order is unaffected.
  const defaultOrder = useMemo(() => {
    // TI's default order is CMS-1862's ranking, which already dropped the
    // invisible cards and put the pinned ones first. The panel's sort below
    // overrides it only when the page offers one: with no option active
    // `applyProductSort` returns its input order, so the ranking stands.
    if (vertical === 'TRAVEL_INSURANCE') {
      return rankedTravelInsurance.map((ranked) => ranked.card);
    }

    return sortProductsByApprovalOdds(
      products,
      (card) => card.productBlock?.product?.title,
      approvalOddsData,
      metadata?.sortByApprovalOdds,
    ).filter((card) => card.visible !== false);
  }, [
    vertical,
    products,
    rankedTravelInsurance,
    approvalOddsData,
    metadata?.sortByApprovalOdds,
  ]);

  // Same call and comparator as the comparison table, so a page rendering both
  // shows one order (req 6 / AC 5c). Memoised: quote and premium updates
  // re-render this component without touching the order.
  // Filter first, then order what survives (R6 / R7.1): sorting must never
  // reintroduce a product the filters removed.
  const productForRender = useMemo(() => {
    const kept = applyProductFilter(
      defaultOrder,
      categories,
      selection,
      (card) => card.productBlock?.product,
    );
    return applyProductSort(
      kept,
      productSortOption,
      (card) => card.productBlock?.product?.attributes,
      // TI's price and discount are quoted per search rather than carried by
      // the product, so they rank on the premium this card was quoted.
      (card) =>
        getTravelInsuranceDataFromProduct(
          card.productBlock?.product,
          travelPremiumsData,
        ),
      // Credit card's gift-value measure ranks on the card's own exclusive
      // offers rather than on an attribute.
      (card) => card.productBlock?.product,
    );
  }, [
    defaultOrder,
    productSortOption,
    categories,
    selection,
    travelPremiumsData,
  ]);

  const showApprovalOdds = Boolean(
    products?.some((p) => p.productBlock?.displayApprovalOdds),
  );

  /**
   * Q7 governs only a list the page sorts; everywhere else CMS-1862 does.
   *
   * Both are requirements about an in-flight re-quote and they demand opposite
   * things: Q7 hides the plans behind skeletons, CMS-1862 keeps them on screen
   * on the fallback price scale. Q7's reason is that the reader must not be
   * shown an order the quote has not been applied to yet, which cannot happen
   * without an active sort option - and there is one exactly when the page
   * offers a Sort By control (`getVisibleSortOptions`), so a filters-only panel
   * or no panel at all keeps CMS-1862's behaviour. See
   * `docs/sort-and-filter.md` § 8.
   */
  const premiumsPending =
    productSortOption !== undefined && arePremiumsPending(travelQuotes);
  const skeletonCount = pendingSkeletonCount(
    skeletonThreshold,
    productForRender.length,
  );

  /**
   * Q7 in the sort-and-filter thread (Clara, 27 Aug) and CMS-1542 R12.3: no
   * plan renders before its premium exists.
   *
   * TI premiums resolve after the page paints on every load, so without this
   * the products render in the configured order and reshuffle when the quote
   * lands — and a price sort makes that reshuffle total. The placeholders
   * `getTravelPremiumsData` injects while a quote is in flight carry an empty
   * `offeredPremium`, which ranks as missing, so the reorder is guaranteed
   * rather than incidental.
   *
   * Skeletons instead of the products, never both: this returns before the
   * cards and before the empty state, which would otherwise read as "nothing
   * matched your filters" while the quote is simply still running.
   */
  if (premiumsPending) {
    return (
      <div
        className={classNames(mobileVisible, 'flex flex-col gap-spacing/gap/y')}
      >
        {Array.from({ length: skeletonCount }).map((_, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <ProductCardSkeleton key={idx} />
        ))}
      </div>
    );
  }

  // R8: the block says nothing matched rather than rendering as an empty
  // column. `FilterEmptyState` itself stays silent when no filter is active, so
  // an empty catalogue is not misattributed to the reader's choices.
  if (productForRender.length === 0) {
    return (
      <div
        className={classNames(mobileVisible, 'flex flex-col gap-spacing/gap/y')}
      >
        <FilterEmptyState />
      </div>
    );
  }

  return (
    <div
      className={classNames(mobileVisible, 'flex flex-col gap-spacing/gap/y')}
    >
      {productForRender.map((p, i) => {
        const product = p.productBlock?.product;
        const eligibility = verdictFor(product);
        const premium = productWithLivePrice?.find(
          (prod) => prod.brokerProductCode === product?.title?.slice(3),
        );

        const carInsuranceQuote = carInsuranceQuotes?.find(
          (quote) => quote.productId === product?.title,
        );

        const travelInsuranceData: TravelPremiumsData[number] | undefined =
          getTravelInsuranceDataFromProduct(product, travelPremiumsData);

        cardsAreShown++;

        const ranking = travelRankingByCard.get(p);
        const showRankingDivider =
          showRankingSource && ranking?.source === 'auto' && ranking.rank === 1;

        const approvalOddsEnabled =
          Boolean(approvalOddsLabels) && showApprovalOdds;
        const productTitle = product?.title;
        const approvalOdds =
          approvalOddsEnabled && productTitle && approvalOddsData
            ? (approvalOddsData.odds[productTitle] ?? null)
            : undefined;
        const approvalOddsState = approvalOddsEnabled
          ? approvalOddsData?.userState
          : undefined;

        return (
          <div
            // The entry's own id, which is required: the product's is nullable
            // twice over, and reconciling by position rewrites every card.
            key={p.id}
            className={classNames({
              'hidden md:block': !p.visible_mobile,
            })}
          >
            {showRankingDivider && (
              <div className="mb-4 flex items-center gap-2" role="separator">
                <span className="grow border-t border-gray-300" />
                <span className="text-xs font-semibold tracking-wide text-gray-600">
                  RANKED BY PRICE ·{' '}
                  {metadata?.priceSortDirection === 'HIGH_TO_LOW'
                    ? 'HIGHEST → LOWEST'
                    : 'LOWEST → HIGHEST'}
                </span>
                <span className="grow border-t border-gray-300" />
              </div>
            )}
            {showRankingSource && ranking && (
              <div className="mb-1 flex justify-end">
                <span className="rounded bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-800">
                  {ranking.source === 'manual'
                    ? `MANUAL · PCT #${ranking.rank}`
                    : `AUTO · PRICE #${ranking.rank}`}
                </span>
              </div>
            )}
            <ProductCard
              product={p.productBlock?.product || {}}
              pageType={pageType}
              vertical={vertical}
              textTag={resolveProductTag({
                product: p.productBlock?.product,
                pageLevelTag: p.productBlock?.text_tag,
              })}
              componentAttributes={
                p?.productBlock?.attributes_list?.filter(Boolean) ?? []
              }
              view_rating={p.productBlock?.view_rating ?? true}
              colorTag={p.productBlock?.color_tag}
              iconTag={p.productBlock?.icon_tag}
              index={i.toString()}
              loanSearchParams={initialLoanSearchParams}
              ctaLinkOverride={p.productBlock?.override_cta_link}
              ctaLabelOverride={p.productBlock?.override_cta_label_long}
              url={url}
              calculateError={calculateError}
              carInsuranceSection={p.productBlock?.car_insurance}
              carInsuranceQuote={carInsuranceQuote}
              productWithLivePrice={premium}
              travelInsuranceQuotes={travelInsuranceData}
              travelQuotes={travelQuotes}
              urlPlanDetails={p.productBlock?.travelInsurance?.urlPlanDetails}
              e2eLabel={p.productBlock?.travelInsurance?.e2eLabel}
              e2eToogleButton={p.productBlock?.travelInsurance?.e2eToggleButton}
              highlightFirstAttribute={metadata?.highlightFirstAttribute}
              statusCarInsuranceRequest={statusCarInsuranceRequest}
              pushDataLayer={pushDataLayer}
              defaultStaticCiTooltip={metadata?.defaultStaticCiTooltip}
              defaultTemplateDynamicCiTooltip={
                metadata?.defaultTemplateDynamicCiTooltip
              }
              plFilterConfig={plFilterConfig}
              approvalOdds={approvalOddsEnabled ? approvalOdds : undefined}
              approvalOddsState={approvalOddsState}
              approvalOddsLabels={
                approvalOddsEnabled ? approvalOddsLabels : undefined
              }
              onApprovalOddsUnlockClick={onApprovalOddsUnlockClick}
              onApprovalOddsRefreshClick={onApprovalOddsRefreshClick}
              productDetailsModalKeys={productDetailsModalKeys}
              getPdpModalConfigAction={getPdpModalConfigAction}
              isPreview={isPreview}
              renderPdpModalHero={renderPdpModalHero}
              eligibility={eligibility}
              // On the card rather than on the wrapper below: dimming the
              // wrapper would dim the Apply button with it, and requirement 4b
              // keeps that at full prominence whatever the outcome.
              deEmphasised={isPlayedDown(eligibility)}
            />
          </div>
        );
      })}
      {!!skeletonThreshold &&
        cardsAreShown < skeletonThreshold &&
        Array.from({ length: skeletonThreshold }).map((_, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <ProductCardSkeleton key={idx} />
        ))}
    </div>
  );
};

/** The card's quoted premium as a number, or undefined while it is unresolved. */
function getLivePremium(
  card: MetadataProductCard,
  travelPremiumsData: TravelPremiumsData | undefined,
): number | undefined {
  const quote = getTravelInsuranceDataFromProduct(
    card.productBlock?.product,
    travelPremiumsData,
  );
  return parsePriceValue(quote?.offeredPremium);
}

/** The card's authored STATIC_PRICE as a number, or undefined when unusable. */
function getStaticPrice(card: MetadataProductCard): number | undefined {
  return parsePriceValue(
    getAttrFromProduct(ATTR_NAMES.STATIC_PRICE, card.productBlock?.product)
      ?.value,
  );
}

/**
 * Resolves the price used for the fallback ranking (CMS-1862). STATIC_PRICE
 * (default) reads the product's STATIC_PRICE attribute; LIVE_PREMIUM reads the
 * quoted premium, so the order follows what the cards actually display.
 *
 * The caller decides which source applies for the whole pool — never per card,
 * which would mix two scales. See the gate at the call site: mid re-quote it
 * swaps to the static scale, but only while that scale orders more of the pool
 * than the premiums currently do.
 */
function getTravelCardPrice(
  card: MetadataProductCard,
  travelPremiumsData: TravelPremiumsData | undefined,
  priceSource: PriceSource | undefined,
): number | undefined {
  if (priceSource === 'LIVE_PREMIUM') {
    return getLivePremium(card, travelPremiumsData);
  }

  return getStaticPrice(card);
}

function getTravelInsuranceDataFromProduct(
  product: CmsProduct | undefined,
  travelPremiumsData: TravelPremiumsData | undefined,
) {
  const attributeIsuemoProductUid = product?.attributes
    ?.filter(
      (a): a is AttrPlainText =>
        a.__typename === 'ComponentAttributesAttrPlainText',
    )
    .find((a) => a.name?.name === ATTR_NAMES.INSUREMO_PRODUCT_UID);

  const travelInsuranceData: TravelPremiumsData[number] | undefined =
    travelPremiumsData?.find(
      (quote) => quote?.brokerProductCode === attributeIsuemoProductUid?.value,
    );

  return travelInsuranceData;
}
